import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  Chip,
  Box,
} from '@mui/material';

interface AnswerEditDialogProps {
  open: boolean;
  questionNumber: number;
  questionText: string;
  currentAnswer: string;
  answerMetadata?: {
    wordCount: number;
    targetWordCount?: number;
    confidenceScore: number;
  };
  onClose: () => void;
  onSave: (editedAnswer: string) => void;
}

export default function AnswerEditDialog({
  open,
  questionNumber,
  questionText,
  currentAnswer,
  answerMetadata,
  onClose,
  onSave,
}: AnswerEditDialogProps) {
  const [editedAnswer, setEditedAnswer] = useState(currentAnswer);
  const [wordCount, setWordCount] = useState(0);

  useEffect(() => {
    setEditedAnswer(currentAnswer);
  }, [currentAnswer, open]);

  useEffect(() => {
    const words = editedAnswer.trim().split(/\s+/).filter(w => w.length > 0);
    setWordCount(words.length);
  }, [editedAnswer]);

  const handleSave = () => {
    onSave(editedAnswer);
    onClose();
  };

  const getWordCountStatus = (): 'default' | 'success' | 'warning' | 'error' => {
    if (!answerMetadata?.targetWordCount) return 'default';

    const target = answerMetadata.targetWordCount;
    const diff = Math.abs(wordCount - target);
    const percentDiff = diff / target;

    if (percentDiff <= 0.2) return 'success'; // Within 20%
    if (percentDiff <= 0.4) return 'warning'; // Within 40%
    return 'error'; // Over 40%
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Answer - Question {questionNumber}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Question:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {questionText}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label={`${wordCount} words`}
              color={getWordCountStatus()}
              size="small"
            />
            {answerMetadata?.targetWordCount && (
              <Chip
                label={`Target: ${answerMetadata.targetWordCount} words`}
                size="small"
                variant="outlined"
              />
            )}
            {answerMetadata?.confidenceScore && (
              <Chip
                label={`Original Confidence: ${Math.round(answerMetadata.confidenceScore * 100)}%`}
                size="small"
                variant="outlined"
              />
            )}
          </Stack>

          <Box>
            <TextField
              multiline
              fullWidth
              rows={15}
              value={editedAnswer}
              onChange={(e) => setEditedAnswer(e.target.value)}
              variant="outlined"
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: 'Georgia, serif',
                  fontSize: '1rem',
                  lineHeight: 1.8,
                },
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!editedAnswer.trim()}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
